import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import {
  BASE_URL,
  getToken,
  getCurrentLanguage,
} from "../../shared/utils.js/utils";

// Get Units Data
export const getUnits = createAsyncThunk(
  "units/getUnits",
  async (params = {}, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();
      if (!token) throw new Error("No authentication token found");
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append("page", params.page);
      if (params.per_page) queryParams.append("per_page", params.per_page);
      if (params.search) queryParams.append("search", params.search);
      if (
        params.is_active !== undefined &&
        params.is_active !== null &&
        params.is_active !== ""
      ) {
        queryParams.append(
          "is_active",
          params.is_active === true || params.is_active === "true" ? "1" : "0",
        );
      }

      const url = `${BASE_URL}/units${
        queryParams.toString() ? `?${queryParams.toString()}` : ""
      }`;
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Accept-Language": currentLang,
        },
      });
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to get units!";
      return rejectWithValue(message);
    }
  },
);

// Get Unit Details
export const getUnitDetails = createAsyncThunk(
  "units/getUnitDetails",
  async (id, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();
      if (!token) throw new Error("No authentication token found");
      const response = await axios.get(`${BASE_URL}/units/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Accept-Language": currentLang,
        },
      });
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to get unit details!";
      return rejectWithValue(message);
    }
  },
);

// delete Units
export const deleteUnits = createAsyncThunk(
  "units/deleteUnits",
  async (id, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();
      if (!token) throw new Error("No authentication token found");
      const response = await axios.delete(`${BASE_URL}/units/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Accept-Language": currentLang,
        },
      });
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to delete unit!";
      return rejectWithValue(message);
    }
  },
);

// Toggle Units Status
export const toggleUnitStatus = createAsyncThunk(
  "units/toggleUnitsStatus",
  async (id, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();
      if (!token) throw new Error("No authentication token found");
      const response = await axios.patch(
        `${BASE_URL}/units/${id}/toggle-status`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Accept-Language": currentLang,
          },
        },
      );
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to toggle unit status!";
      return rejectWithValue(message);
    }
  },
);

// Add Unit
export const addUnit = createAsyncThunk(
  "units/addUnit",
  async (unitData, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();

      if (!token) {
        throw new Error("No authentication token found");
      }

      const response = await axios.post(`${BASE_URL}/units`, unitData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Accept-Language": currentLang,
        },
      });
      return response.data;
    } catch (error) {
      if (error.response?.data?.errors) {
        const errorMessages = Object.values(error.response.data.errors).flat();
        return rejectWithValue(errorMessages.join(", "));
      }
      const message = error.response?.data?.message || "Failed to add unit!";
      return rejectWithValue(message);
    }
  },
);

// update Unit
export const updateUnit = createAsyncThunk(
  "units/updateUnit",
  async ({ id, unitData }, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();

      if (!token) {
        throw new Error("No authentication token found");
      }

      const response = await axios.put(`${BASE_URL}/units/${id}`, unitData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Accept-Language": currentLang,
        },
      });
      return response.data;
    } catch (error) {
      if (error.response?.data?.errors) {
        const errorMessages = Object.values(error.response.data.errors).flat();
        return rejectWithValue(errorMessages.join(", "));
      }
      const message = error.response?.data?.message || "Failed to update unit!";
      return rejectWithValue(message);
    }
  },
);

// Get Measurables
export const getMeasurables = createAsyncThunk(
  "units/getMeasurables",
  async (params = {}, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();
      if (!token) throw new Error("No authentication token found");
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append("page", params.page);
      if (params.per_page) queryParams.append("per_page", params.per_page);
      if (params.search) queryParams.append("search", params.search);
      if (params.type) queryParams.append("type", params.type);
      if (
        params.is_active !== undefined &&
        params.is_active !== null &&
        params.is_active !== ""
      ) {
        queryParams.append("is_active", params.is_active);
      }

      const url = `${BASE_URL}/measurables${
        queryParams.toString() ? `?${queryParams.toString()}` : ""
      }`;
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Accept-Language": currentLang,
        },
      });
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to get measurables!";
      return rejectWithValue(message);
    }
  },
);
